/**
 * HDR photos as a JPEG with a gain map (1.179.0, #155, `hdr_photo`).
 *
 * **What an HDR photo is.** An SDR picture plus a second, small JPEG behind
 * it -- the gain map -- that tells an HDR screen how far each point may
 * brighten; the base picture is what every other screen shows (#157). Two
 * forms are in use, and both put the second JPEG behind the first and list
 * it in an MPF segment (CIPA DC-007): **Ultra HDR** (Android, Chrome) names
 * it in an XMP block of the primary (`hdrgm:Version`, a `Container:Directory`
 * with the map's length) and keeps the map's metadata in the map's own XMP
 * (`hdrgm:GainMapMin` and so on); **ISO 21496-1** (an iPhone since iOS 18)
 * keeps the metadata in a binary APP2 segment, in the primary and in the map.
 *
 * **What is done here.** Reducing a photo redraws it onto a canvas, and the
 * drawn JPEG has no map. So the map is taken out of the original, reduced by
 * the same factor, and both are put together again: the metadata segments go
 * over **as they are** -- they describe how values map, not how many points
 * there are -- and only what names bytes is written anew (the primary's
 * Ultra HDR XMP with the map's new length, the MPF). Written is the Ultra
 * HDR form, with the ISO segments beside it where the original had them: a
 * Chrome reads the first, an iPhone the second.
 *
 * **Decided from the file, like the 360° photo.** A JPEG whose primary names
 * a map and whose second picture is there has one; anything else is a
 * photo. The server reads the same marks (`GainMap.php`) and believes
 * nothing the browser says. **Not measured on a device**: this Chromium
 * draws no HDR, the bench holds the round trip and the server's flag.
 */
import { isJpeg } from './exif'
import { APP1, APP2, EOI, SOS, XMP_HEADER, behindLeadingApps, encode, join, payload, segment, segmentsOf, startsWith, xmpOf } from './jpegSegments'
import type { Segment } from './jpegSegments'

const ISO_HEADER = 'urn:iso:std:iso:ts:21496:-1\0'
const MPF_HEADER = 'MPF\0'

/**
 * Where a JPEG ends: behind its last scan at the `EOI`. Inside scan data a
 * `0xff` is followed by `0x00` or a restart marker, so a marker found there
 * is a marker; the thumbnail inside an EXIF block has an `EOI` of its own,
 * which is why the segments at the front are stepped over by their length.
 */
function endOf(jpeg: Uint8Array, from = 0): number | null {
  const head = segmentsOf(jpeg, from)
  const last = head[head.length - 1]
  let at = last === undefined ? from + 2 : last.start + last.size
  while (at + 1 < jpeg.length) {
    if (jpeg[at] !== 0xff) {
      at += 1
      continue
    }
    const marker = jpeg[at + 1] ?? 0
    if (marker === 0x00 || marker === 0xff || (marker >= 0xd0 && marker <= 0xd7)) {
      at += marker === 0xff ? 1 : 2
      continue
    }
    if (marker === EOI) return at + 2
    if (marker === SOS) {
      // The scan header has a length; the data behind it has none.
      const size = ((jpeg[at + 2] ?? 0) << 8) | (jpeg[at + 3] ?? 0)
      at += 2 + Math.max(2, size)
      continue
    }
    const size = ((jpeg[at + 2] ?? 0) << 8) | (jpeg[at + 3] ?? 0)
    at += 2 + Math.max(2, size)
  }
  return null
}


/** The same three marks the server reads (`GainMap::hasGainMap()`). */
export function namesGainMap(jpeg: Uint8Array): boolean {
  if (!isJpeg(jpeg)) return false
  const xmp = xmpOf(jpeg)
  if (xmp !== null && (xmp.includes('hdrgm:Version') || xmp.includes('HDRGainMap:'))) return true
  return segmentsOf(jpeg).some((s) => s.marker === APP2 && startsWith(payload(jpeg, s), ISO_HEADER))
}

/** The metadata a map carries along: its XMP, its ISO segment -- whole segments, marker included. */
export interface MapMetadata {
  /** The ISO 21496-1 segment of the primary, if any. */
  primaryIso: Uint8Array | null
  /** The map's own XMP (`hdrgm:GainMapMin` …) and ISO segment, if any. */
  mapXmp: Uint8Array | null
  mapIso: Uint8Array | null
}

export interface SplitGainMap {
  /** The primary picture alone, up to and including its `EOI`. */
  primary: Uint8Array
  /** The gain map, a JPEG of its own. */
  map: Uint8Array
  meta: MapMetadata
}

/**
 * The two pictures of an HDR JPEG, or null for a photo without a map. The
 * map is found behind the primary's end, not through the MPF offsets: the
 * offsets are relative to the MPF segment and wrong in files that were
 * re-saved by a tool that did not know them.
 */
export function splitGainMap(jpeg: Uint8Array): SplitGainMap | null {
  if (!namesGainMap(jpeg)) return null
  const end = endOf(jpeg)
  if (end === null) return null
  // The map begins at the next SOI; a few bytes of padding are allowed.
  let at = end
  while (at + 1 < jpeg.length && !(jpeg[at] === 0xff && jpeg[at + 1] === 0xd8)) at += 1
  if (at + 4 > jpeg.length) return null
  const mapEnd = endOf(jpeg, at)
  if (mapEnd === null) return null
  const map = jpeg.subarray(at, mapEnd)
  const whole = (bytes: Uint8Array, s: Segment | undefined): Uint8Array | null =>
    s === undefined ? null : bytes.subarray(s.start, s.start + s.size)
  const mapSegments = segmentsOf(map)
  const meta: MapMetadata = {
    primaryIso: whole(jpeg, segmentsOf(jpeg).find((s) => s.marker === APP2 && startsWith(payload(jpeg, s), ISO_HEADER))),
    mapXmp: whole(map, mapSegments.find((s) => s.marker === APP1 && startsWith(payload(map, s), XMP_HEADER))),
    mapIso: whole(map, mapSegments.find((s) => s.marker === APP2 && startsWith(payload(map, s), ISO_HEADER))),
  }
  // A map without any metadata is not one this code can carry over.
  if (meta.mapXmp === null && meta.mapIso === null && meta.primaryIso === null) return null
  return { primary: jpeg.subarray(0, end), map, meta }
}

/** The primary's Ultra HDR XMP: the version, and the directory with the map's length. */
function primaryXmp(mapLength: number): Uint8Array {
  const xmp = '<?xpacket begin="﻿" id="W5M0MpCehiHzreSzNTczkc9d"?>'
    + '<x:xmpmeta xmlns:x="adobe:ns:meta/"><rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">'
    + '<rdf:Description rdf:about=""'
    + ' xmlns:Container="http://ns.google.com/photos/1.0/container/"'
    + ' xmlns:Item="http://ns.google.com/photos/1.0/container/item/"'
    + ' xmlns:hdrgm="http://ns.adobe.com/hdr-gain-map/1.0/"'
    + ' hdrgm:Version="1.0">'
    + '<Container:Directory><rdf:Seq>'
    + '<rdf:li rdf:parseType="Resource"><Container:Item Item:Semantic="Primary" Item:Mime="image/jpeg"/></rdf:li>'
    + `<rdf:li rdf:parseType="Resource"><Container:Item Item:Semantic="GainMap" Item:Mime="image/jpeg" Item:Length="${mapLength}"/></rdf:li>`
    + '</rdf:Seq></Container:Directory>'
    + '</rdf:Description></rdf:RDF></x:xmpmeta><?xpacket end="w"?>'
  const body = new Uint8Array(XMP_HEADER.length + encode(xmp).length)
  body.set(encode(XMP_HEADER))
  body.set(encode(xmp), XMP_HEADER.length)
  return segment(APP1, body)
}

/**
 * The MPF segment (CIPA DC-007, big-endian): version, two pictures, and an
 * entry each with size and offset. The offset counts from the MP header,
 * which is where the TIFF bytes begin, eight bytes into the payload after
 * `MPF\0`; the primary's own offset is zero by the standard.
 */
function mpfSegment(primarySize: number, mapSize: number, mapOffset: number): Uint8Array {
  const body = new Uint8Array(4 + 8 + 2 + 3 * 12 + 4 + 2 * 16)
  const view = new DataView(body.buffer)
  body.set(encode(MPF_HEADER), 0)
  let at = 4
  body.set([0x4d, 0x4d, 0x00, 0x2a], at) // 'MM' and 42
  view.setUint32(at + 4, 8)
  at += 8
  view.setUint16(at, 3)
  at += 2
  // MPFVersion, UNDEFINED, four bytes '0100' in place.
  view.setUint16(at, 0xb000); view.setUint16(at + 2, 7); view.setUint32(at + 4, 4); body.set(encode('0100'), at + 8)
  at += 12
  // NumberOfImages, LONG.
  view.setUint16(at, 0xb001); view.setUint16(at + 2, 4); view.setUint32(at + 4, 1); view.setUint32(at + 8, 2)
  at += 12
  // MPEntry, UNDEFINED, 16 bytes per picture, behind the IFD.
  const entries = 8 + 2 + 3 * 12 + 4
  view.setUint16(at, 0xb002); view.setUint16(at + 2, 7); view.setUint32(at + 4, 32); view.setUint32(at + 8, entries)
  at += 12
  view.setUint32(at, 0) // no next IFD
  at += 4
  // The primary: baseline MP primary image, representative.
  view.setUint32(at, 0x030000); view.setUint32(at + 4, primarySize); view.setUint32(at + 8, 0)
  at += 16
  // The map: an undefined type, as Ultra HDR writes it.
  view.setUint32(at, 0); view.setUint32(at + 4, mapSize); view.setUint32(at + 8, mapOffset)
  return segment(APP2, body)
}

/**
 * The base picture and the reduced map as one HDR JPEG. `base` is the drawn
 * JPEG with its EXIF already in place; `map` the reduced map as a plain
 * JPEG. The metadata segments go in as they came.
 */
export function withGainMap(base: Uint8Array, map: Uint8Array, meta: MapMetadata): Uint8Array<ArrayBuffer> {
  // The map: SOI, its metadata, then the rest of the drawn JPEG.
  const mapParts: Uint8Array[] = [new Uint8Array([0xff, 0xd8])]
  if (meta.mapXmp !== null) mapParts.push(meta.mapXmp)
  if (meta.mapIso !== null) mapParts.push(meta.mapIso)
  mapParts.push(map.subarray(2))
  const mapOut = join(mapParts)

  // The primary: behind SOI and the APP0/APP1 at the front (JFIF, EXIF),
  // as `withSphereXmp()` places its block, come XMP, MPF and the ISO segment.
  const at = behindLeadingApps(base)
  const xmp = primaryXmp(mapOut.length)
  const mpfLength = mpfSegment(0, 0, 0).length
  const iso = meta.primaryIso ?? new Uint8Array(0)
  const primarySize = base.length + xmp.length + mpfLength + iso.length
  // The MP header lies eight bytes into the MPF payload: marker (2), length (2), 'MPF\0' (4).
  const mpHeader = at + xmp.length + 8
  const mpf = mpfSegment(primarySize, mapOut.length, primarySize - mpHeader)
  return join([base.subarray(0, at), xmp, mpf, iso, base.subarray(at), mapOut])
}

/**
 * The values of ISO 21496-1, in the format's own units (1.181.0): the gain
 * and the headroom as log2, the gamma and the offsets as they are. Read to
 * write the same values a second time in Adobe's words (`hdrgm`), which is
 * what an Ultra HDR reader before ISO looks for; the ISO bytes themselves go
 * into the JPEG unchanged.
 */
export interface IsoGainMap {
  baseHeadroom: number
  alternateHeadroom: number
  channels: { min: number; max: number; gamma: number; baseOffset: number; alternateOffset: number }[]
}

/**
 * The binary metadata (ISO 21496-1, clause C.2): two version numbers, a byte
 * of flags (bit 7 three channels, bit 3 one common denominator), the two
 * headrooms and, per channel, minimum, maximum, gamma and the two offsets,
 * each a fraction. Null for a version this code does not know.
 */
export function readIsoGainMap(iso: Uint8Array): IsoGainMap | null {
  if (iso.length < 5) return null
  const view = new DataView(iso.buffer, iso.byteOffset, iso.byteLength)
  if (view.getUint16(0) !== 0) return null
  const flags = view.getUint8(4)
  const channels = flags & 0x80 ? 3 : 1
  const common = (flags & 0x08) !== 0
  let p = 5
  const need = common ? 4 + 8 + channels * 20 : 16 + channels * 40
  if (iso.length < p + need) return null
  const denominator = common ? view.getUint32(p) : 0
  if (common) p += 4
  const fraction = (signed: boolean): number => {
    const n = signed ? view.getInt32(p) : view.getUint32(p)
    p += 4
    if (common) return denominator === 0 ? NaN : n / denominator
    const d = view.getUint32(p)
    p += 4
    return d === 0 ? NaN : n / d
  }
  const baseHeadroom = fraction(false)
  const alternateHeadroom = fraction(false)
  const out: IsoGainMap['channels'] = []
  for (let c = 0; c < channels; c++) {
    out.push({ min: fraction(true), max: fraction(true), gamma: fraction(false), baseOffset: fraction(true), alternateOffset: fraction(true) })
  }
  const all = [baseHeadroom, alternateHeadroom, ...out.flatMap((c) => Object.values(c))]
  return all.every(Number.isFinite) ? { baseHeadroom, alternateHeadroom, channels: out } : null
}

const decimal = (v: number): string => String(Number(v.toFixed(6)))

/** The map's Ultra HDR XMP (`hdrgm`, Adobe's gain map specification 1.0) for these values. */
function mapXmpFor(gm: IsoGainMap): Uint8Array {
  const per = (pick: (c: IsoGainMap['channels'][number]) => number): { attr: string | null; seq: string } => {
    const values = gm.channels.map(pick)
    return values.every((v) => v === values[0])
      ? { attr: decimal(values[0]!), seq: '' }
      : { attr: null, seq: `<rdf:Seq>${values.map((v) => `<rdf:li>${decimal(v)}</rdf:li>`).join('')}</rdf:Seq>` }
  }
  const fields: [string, ReturnType<typeof per>][] = [
    ['GainMapMin', per((c) => c.min)],
    ['GainMapMax', per((c) => c.max)],
    ['Gamma', per((c) => c.gamma)],
    ['OffsetSDR', per((c) => c.baseOffset)],
    ['OffsetHDR', per((c) => c.alternateOffset)],
  ]
  const attrs = fields.filter(([, f]) => f.attr !== null).map(([name, f]) => ` hdrgm:${name}="${f.attr}"`).join('')
  const elements = fields.filter(([, f]) => f.attr === null).map(([name, f]) => `<hdrgm:${name}>${f.seq}</hdrgm:${name}>`).join('')
  const baseIsHdr = gm.baseHeadroom > gm.alternateHeadroom
  const xmp = '<?xpacket begin="﻿" id="W5M0MpCehiHzreSzNTczkc9d"?>'
    + '<x:xmpmeta xmlns:x="adobe:ns:meta/"><rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">'
    + '<rdf:Description rdf:about="" xmlns:hdrgm="http://ns.adobe.com/hdr-gain-map/1.0/" hdrgm:Version="1.0"'
    + attrs
    + ` hdrgm:HDRCapacityMin="${decimal(Math.min(gm.baseHeadroom, gm.alternateHeadroom))}"`
    + ` hdrgm:HDRCapacityMax="${decimal(Math.max(gm.baseHeadroom, gm.alternateHeadroom))}"`
    + ` hdrgm:BaseRenditionIsHDR="${baseIsHdr ? 'True' : 'False'}"`
    + (elements === '' ? '/>' : `>${elements}</rdf:Description>`)
    + '</rdf:RDF></x:xmpmeta><?xpacket end="w"?>'
  return segment(APP1, join([encode(XMP_HEADER), encode(xmp)]))
}

/**
 * The metadata of a gain map that came without a JPEG around it, from a
 * HEIC's `tmap` (1.181.0, `lib/heifGainMap.ts`): the ISO bytes as they are,
 * in the map and as their two version numbers in the primary (what
 * Lightroom writes there), and Adobe's XMP from the same values. Null if
 * the metadata do not read.
 */
export function metadataFromIso(iso: Uint8Array): MapMetadata | null {
  const gm = readIsoGainMap(iso)
  if (gm === null) return null
  return {
    primaryIso: segment(APP2, join([encode(ISO_HEADER), iso.subarray(0, 4)])),
    mapIso: segment(APP2, join([encode(ISO_HEADER), iso])),
    mapXmp: mapXmpFor(gm),
  }
}
